import { useEffect, useRef, useState } from 'react';
import Hls from 'hls.js';

export type EstadoReproduccion = 'conectando' | 'enVivo' | 'reconectando' | 'sinSenal';

/** Espera antes de rehacer el reproductor. Crece con los intentos fallidos. */
const ESPERA_BASE_MS = 3000;
const ESPERA_MAXIMA_MS = 30_000;

/**
 * Cuánto puede quedarse quieta la imagen antes de darla por congelada.
 *
 * Un directo con cortes de red normales recupera en dos o tres segundos. Doce
 * dejan margen de sobra para eso y siguen siendo poco tiempo mirando una foto
 * fija creyendo que es vídeo en vivo.
 */
const CONGELADO_MS = 12_000;

/** Cada cuánto se comprueba que la imagen sigue avanzando. */
const VIGILANCIA_MS = 3000;

/**
 * Cuántas veces se intenta despertar la emisión sin rehacer el reproductor.
 * Pasadas, se levanta de cero: insistir sobre una sesión rota no la arregla.
 */
const INTENTOS_SUAVES = 2;

/** Margen que se le da a un intento suave antes de darlo por fallido. */
const MARGEN_INTENTO_MS = 4000;

/**
 * Fallos de red seguidos que se intentan recuperar sin tocar el reproductor,
 * antes de levantarlo entero. `startLoad` resuelve un corte pasajero; cuando
 * son varios, lo que hay roto es la sesión.
 */
const REINTENTOS_RED = 3;

/**
 * Reproduce el vídeo en vivo de una cámara y se encarga de que siga en vivo.
 *
 * El navegador no sabe reproducir RTSP, así que lo que llega aquí es HLS: el
 * servidor de vídeo hace la conversión y publica una lista de reproducción que
 * sí entiende. Safari la reproduce por su cuenta; el resto necesita `hls.js`.
 *
 * Lo importante no es arrancar, es no quedarse colgado. Una emisión se congela
 * de tres maneras y sólo una de ellas avisa:
 *
 *  1. Error fatal — hls.js lo notifica y se puede actuar.
 *  2. Imagen quieta sin error — el reproductor cree que va bien y la hora
 *     grabada en la imagen se queda parada. Nadie lo detecta salvo mirando si
 *     el tiempo del vídeo avanza, que es lo que hace el vigilante de aquí.
 *  3. Pestaña en segundo plano — el navegador frena la descarga y al volver la
 *     emisión está minutos por detrás. Técnicamente reproduce; en la práctica
 *     estás viendo el pasado, que en vigilancia es peor que no ver nada.
 *
 * Cada cámara se recupera por separado: si una se cae, las demás siguen. Y al
 * desmontar se destruye todo, que es lo que libera el ancho de banda —cada
 * cámara abierta consume del orden de un megabyte y medio por segundo.
 */
export function useReproductorHls(url: string | null, activo = true) {
  const video = useRef<HTMLVideoElement | null>(null);
  const [estado, setEstado] = useState<EstadoReproduccion>('conectando');

  useEffect(() => {
    const elemento = video.current;

    /*
     * `activo` en falso desmonta el reproductor por completo.
     *
     * Al abrir una cámara a pantalla completa las demás dejan de hacer falta, y
     * dejarlas corriendo cuesta caro: cada una ocupa del orden de un megabyte y
     * medio por segundo de red y un descodificador de vídeo. Con nueve abiertas
     * el navegador se arrastra y el grabador sirve nueve sesiones para que
     * alguien mire una.
     */
    if (!elemento || !url || !activo) return;

    let reproductor: Hls | null = null;
    let reintento: number | undefined;
    let vigilante: number | undefined;
    let fallosDeRed = 0;
    let intentos = 0;
    // Evita que un temporizador o un error tardío toquen algo ya desmontado.
    let vigente = true;

    // Último avance observado, para distinguir «va lento» de «está congelado».
    let ultimoTiempo = -1;
    let ultimoAvance = Date.now();

    /*
     * Cuántas veces seguidas se ha intentado despertarla sin rehacer el
     * reproductor. Es lo que impide quedarse dando saltos al directo para
     * siempre sobre una sesión que ya no sirve.
     */
    let intentosSuaves = 0;

    const marcarAvance = () => {
      ultimoTiempo = elemento.currentTime;
      ultimoAvance = Date.now();
    };

    /** Salta al momento actual de la emisión, descartando lo acumulado. */
    const irAlDirecto = () => {
      if (reproductor && reproductor.liveSyncPosition !== null) {
        elemento.currentTime = reproductor.liveSyncPosition;
        return;
      }

      // Sin hls.js (Safari) el final del buffer es lo más cercano al directo.
      const buffer = elemento.buffered;
      if (buffer.length > 0) elemento.currentTime = buffer.end(buffer.length - 1);
    };

    const programarReinicio = () => {
      if (!vigente) return;

      reproductor?.destroy();
      reproductor = null;

      const espera = Math.min(ESPERA_BASE_MS * 2 ** intentos, ESPERA_MAXIMA_MS);
      intentos += 1;
      window.clearTimeout(reintento);
      reintento = window.setTimeout(arrancar, espera);
    };

    const arrancar = () => {
      if (!vigente) return;

      setEstado(intentos === 0 ? 'conectando' : 'reconectando');
      fallosDeRed = 0;
      marcarAvance();

      // Safari (y iOS) reproducen HLS de forma nativa, sin librería.
      if (elemento.canPlayType('application/vnd.apple.mpegurl')) {
        elemento.src = url;
        void elemento.play().catch(() => undefined);
        return;
      }

      if (!Hls.isSupported()) {
        setEstado('sinSenal');
        return;
      }

      reproductor = new Hls({
        liveSyncDurationCount: 3,
        liveMaxLatencyDurationCount: 6,
        enableWorker: true,
        lowLatencyMode: true,
      });

      reproductor.loadSource(url);
      reproductor.attachMedia(elemento);

      reproductor.on(Hls.Events.MANIFEST_PARSED, () => {
        if (!vigente) return;
        void elemento.play().catch(() => undefined);
      });

      reproductor.on(Hls.Events.ERROR, (_evento, datos) => {
        if (!datos.fatal || !vigente) return;

        if (datos.type === Hls.ErrorTypes.NETWORK_ERROR) {
          fallosDeRed += 1;
          setEstado('reconectando');

          if (fallosDeRed <= REINTENTOS_RED) {
            reproductor?.startLoad();
            return;
          }

          // Insistir con la misma sesión ya no lleva a ninguna parte.
          setEstado('sinSenal');
          programarReinicio();
          return;
        }

        if (datos.type === Hls.ErrorTypes.MEDIA_ERROR) {
          setEstado('reconectando');
          reproductor?.recoverMediaError();
          return;
        }

        setEstado('sinSenal');
        programarReinicio();
      });
    };

    /*
     * Confirmar que hay imagen es lo único que distingue «en vivo» de «una foto
     * fija»: los eventos del reproductor pueden decir que todo va bien mientras
     * la imagen lleva un minuto parada.
     */
    const alAvanzar = () => {
      if (!vigente) return;
      marcarAvance();
      // Se ha recuperado sola: las cuentas vuelven a cero para que el siguiente
      // corte no arrastre la espera larga de un problema ya pasado.
      intentos = 0;
      intentosSuaves = 0;
      setEstado('enVivo');
    };

    elemento.addEventListener('timeupdate', alAvanzar);

    vigilante = window.setInterval(() => {
      if (!vigente || document.hidden) return;

      if (elemento.currentTime !== ultimoTiempo) {
        marcarAvance();
        return;
      }

      if (Date.now() - ultimoAvance < CONGELADO_MS) return;

      // La imagen lleva parada demasiado tiempo.
      setEstado('reconectando');

      /*
       * Primero se prueba lo barato —saltar al directo y reanudar—, pero sólo
       * un par de veces.
       *
       * Antes se intentaba siempre esto y nunca se pasaba a rehacer el
       * reproductor, porque cada intento reiniciaba el reloj de congelación.
       * El resultado era una cámara diciendo «Reconectando…» toda la noche sin
       * volver jamás: la sesión estaba muerta y nadie la enterraba.
       */
      if (reproductor && intentosSuaves < INTENTOS_SUAVES) {
        intentosSuaves += 1;
        irAlDirecto();
        void elemento.play().catch(() => undefined);
        // Se le da un margen para responder, no el plazo entero.
        ultimoAvance = Date.now() - CONGELADO_MS + MARGEN_INTENTO_MS;
        reproductor.startLoad();
        return;
      }

      // Ni con esas: se levanta el reproductor de cero.
      intentosSuaves = 0;
      programarReinicio();
    }, VIGILANCIA_MS);

    /*
     * Al volver a la pestaña la emisión está tan atrás como haya durado la
     * ausencia. Saltar al directo evita quedarse mirando lo que pasó hace un
     * rato creyendo que es ahora.
     */
    const alCambiarVisibilidad = () => {
      if (document.hidden || !vigente) return;
      irAlDirecto();
      void elemento.play().catch(() => undefined);
      marcarAvance();
    };

    document.addEventListener('visibilitychange', alCambiarVisibilidad);

    arrancar();

    return () => {
      vigente = false;
      window.clearTimeout(reintento);
      window.clearInterval(vigilante);
      elemento.removeEventListener('timeupdate', alAvanzar);
      document.removeEventListener('visibilitychange', alCambiarVisibilidad);
      reproductor?.destroy();

      try {
        elemento.pause();
        elemento.removeAttribute('src');
        elemento.load();
      } catch {
        // El elemento ya no existe; no hay nada que liberar.
      }
    };
  }, [url, activo]);

  return { video, estado };
}
